export interface Organization {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  website?: string;
  email?: string;
  phone_number?: string;
  address?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  owner: number;
  subscription?: OrganizationSubscription;
}

export interface OrganizationAICustomInstruction {
  id: number;
  context_type: string;
  instruction_text: string;
  active: boolean;
}

export interface OrganizationSubscription {
  id: number;
  organization: {
    id: string;
    name: string;
    created_at: string;
  };
  tier: string;
  tier_display: string;
  date_joined: string;
  is_trial: boolean;
  trial_days: number;
  billing_cycle_days: number;
  next_billing_date: string | null;
  auto_renew: boolean;
}

export interface OrganizationPaymentRecord {
  id: number;
  date: string;
  amount: number;
  status: 'SUCCESS' | 'FAILED' | 'PENDING';
  reference: string;
}

export interface Company {
  id: string;
  name: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  created_at?: string;
  updated_at?: string;
}

export const getCompanyName = (company?: Company | string | null) => {
  if (!company) {
    return '';
  }

  if (typeof company === 'string') {
    return company;
  }

  return company.name || '';
};

export const getCompanyId = (company?: Company | string | null) => {
  if (!company || typeof company === 'string') {
    return '';
  }

  return company.id || '';
};

export type EventType =
  | 'follow_up'
  | 'lead_followup'
  | 'meeting'
  | 'deadline'
  | 'task'
  | 'custom';
export type EventStatus = 'scheduled' | 'upcoming' | 'completed' | 'cancelled';

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start_datetime: string; // ISO string
  end_datetime?: string; // ISO string
  event_type: EventType;
  status: EventStatus;
  organization: string;
  assigned_to?: string;
  assigned_to_name?: string | null;
  created_by?: string;
  created_by_name?: string | null;
  related_task?: string | null;
  related_lead?: string | number | null;
  related_source?: 'task' | 'lead_followup' | null;
  created_at?: string;
  updated_at?: string;
}

export interface Lead {
  id: number;
  title: string;
  first_name: string;
  second_name: string | null;
  company: Company | string | null;
  added_by: string | null;
  assigned_to: string | null;
  name_of_employee_assigned_to: string | null;
  email: string;
  phone: string;
  stage:
    | 'new'
    | 'contacted'
    | 'qualified'
    | 'lost'
    | 'converted'
    | 'proposal'
    | 'won'
    | string;
  level_of_interest:
    | 'HIGHLY'
    | 'MEDIUM_INTEREST'
    | 'INTERESTED'
    | 'NEUTRAL'
    | 'NOT_INTERESTED'
    | string;
  level_of_interest_display:
    | 'Highly Interested'
    | 'Medium Interest'
    | 'Interested'
    | 'Neutral'
    | 'Not Interested'
    | string;
  created_at: string;
  last_contacted: string | null;
  next_follow_up: string | null;
  organization: string;
  primary_source?: string | null;
  source_detail?: string | null;
  last_inbound_channel?: string | null;
  source_metadata?: Record<string, unknown>;
  channel_identities?: LeadChannelIdentity[];
  lead_notes?: LeadNote[];
  lead_interests?: LeadInterest[];
}

export interface LeadChannelIdentity {
  id: number;
  channel: string;
  external_user_id?: string | null;
  external_thread_id?: string | null;
  phone?: string | null;
  email?: string | null;
  username?: string | null;
  display_name?: string | null;
  normalized_contact?: string | null;
  first_seen_at?: string;
  last_seen_at?: string;
  metadata?: Record<string, unknown>;
}

export interface LeadNote {
  id: number;
  content: string;
  created_at: string;
  lead: number;
  written_by: number | null;
  created_by: number | null;
  organization: string | null;
}
export interface Catalogue {
  id: number;
  name: string;
  description: string;
  price: number;
  created_at?: string;
  organization?: string;
}

export interface CatalogueItemImage {
  id: string;
  image: string;
  uploaded_at?: string;
  created_at?: string;
}

export interface CatalogueItem {
  id: string;
  name: string;
  description: string;
  price: number;
  created_at?: string;
  updated_at?: string;
  waranty?: number | null;
  last_negotiation_price?: number | null;
  is_active: boolean;
  images: CatalogueItemImage[];
  organization?: string;
}

export interface LeadInterest {
  id: number;
  lead: number;
  interest: CatalogueItem;
  created_at: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface Customer {
  id: string; // UUID or numeric ID
  organization_id: string; // UUID of the organization this customer belongs to
  first_name: string;
  second_name: string;
  display_name: string;
  title: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  facebook_id?: string;
  instagram_id?: string;
  source?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  company_name?: string;
  lead: Lead; // if linked from a lead
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
}

export interface CustomerEdit {
  id?: string;
  organization_id?: string; // UUID of the organization this customer belongs to
  first_name?: string;
  second_name?: string;
  display_name?: string;
  title: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  facebook_id?: string;
  instagram_id?: string;
  source?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  company_name?: string;
}

export interface QuoteItem {
  id: number;
  item_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface Quote {
  id: string;
  items: QuoteItem[];
  total_amount: number;
  reference_number: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected'; // extendable
  notes: string;
  valid_until: string | null;
  created_at: string;
  customer?: Customer;
  organization: Organization;
  created_by: number | null;
  lead?: Lead;
}

// export interface WhatsappNumber {
//   id: string;
//   phone_number: string;
//   email: string;
//   website: string;
//   address: string;
//   about: string;
//   display_name: string;
//   status: string;
//   webhook_verified: boolean;
//   ai_chat_responses: boolean;
//   away_message_enabled: boolean;
//   away_message_text: string;
// }

export interface WhatsappNumber {
  id: string;

  // Business info
  display_name: string | null;
  business_name?: string | null;
  about: string | null;
  website: string | null;
  address: string | null;
  email: string | null;

  // Phone & Meta
  phone_number: string | null;
  phone_number_id: string | null;
  whatsapp_business_account_id: string | null;
  meta_business_account_id: string | null;

  // Settings
  ai_chat_responses: boolean;
  status: 'active' | 'disabled';

  // System
  created_at: string;
  last_sync_at: string | null;
}

export interface FacebookProfile {
  id: string; // UUID (from backend)
  page_id: string; // Facebook Page ID
  page_name: string; // Page name from Facebook
  ai_chat_responses: boolean;
  ai_comment_responses: boolean;
  oauth_temp_code?: string | null;
  profile_picture_url?: string | null;
  connected_at: string; // ISO timestamp
  is_active: boolean;
  short_term_access_token?: string | null;
  access_token?: string | null;
  metadata?: Record<string, any> | null;
}

export interface instagramAccountDetails {
  id: string;
  phone_number?: string;
  display_name?: string;
  username: string;
  status?: string;
  webhook_verified?: boolean;
  ai_comment_responses?: boolean;
  ai_chat_responses?: boolean;
  is_active?: boolean;
  last_refreshed?: string;
}

export interface FacebookLastMessage {
  id: string; // UUID
  conversation: string; // UUID
  sender_id: string | null; // sender_id from backend
  text: string | null; // message text
  preview_text?: string | null;
  timestamp: string | null; // ISO timestamp
  delivery_status: string; // backend sends this
  direction: 'incoming' | 'outgoing' | 'system' | string;
  metadata: Record<string, any>; // message metadata
}

export interface LastMessage {
  id: string; // UUID
  conversation: string; // UUID
  sender_id: string | null; // sender_id from backend
  text: string | null; // message text
  timestamp: string | null; // ISO timestamp
  delivery_status: string; // added because backend sends it
  direction: string; // incoming/outgoing
  metadata: Record<string, any>; // message metadata
}

export interface WhatsappConversation {
  id: number;
  lead: Lead;
  channel?: string;
  started_at?: string;
  is_active: boolean;
  last_message: LastMessage | null;
  recipient_phone: string;
  open_window_expires_at: string | null;
  created_at?: string;
  external_id?: string | null;
  metadata?: Record<string, any>;
  last_message_at?: string | null;
  last_incoming_message_at?: string | null;
  whatsapp_number?: string | number | null;
  ai_chat_responses?: boolean;
}

export interface InstagramConversationParticipant {
  id: string; // IG user ID
  username?: string; // optional
}

export interface InstagramLastMessage {
  id: string; // optional if you track message IDs
  text: string; // snippet/content of last message
  created_at: string; // ISO timestamp
  sender_id: string; // IG sender ID
}

export interface InstagramConversation {
  id: string; // UUID
  thread_id?: string; // Meta API thread ID
  profile_picture?: string;
  instagram_account_id?: string; // IG account ID this conversation belongs to
  participants?: InstagramConversationParticipant[]; // all participants including self
  participant_ig_id?: string; // main participant IG ID (other than self)
  participant_username?: string; // optional username
  snippet?: string; // last message snippet
  created_at?: string; // ISO timestamp
  last_message_at?: string; // ISO timestamp of last message
  updated_time?: string; // ISO timestamp
  last_message?: InstagramMessage | null; // optional nested last message object
  messages?: InstagramMessage[]; // optional array of messages in this conversation
  ai_chat_responses?: boolean;
}

export interface FacebookUtilityMessage {
  id: string; // UUID from backend

  // Template identity
  template_name: string;
  language: string; // e.g. "en"
  category: string; // "UTILITY"

  // Content
  body_text: string;
  trigger: 'ACCOUNT_UPDATE' | 'POST_PURCHASE_UPDATE' | 'CONFIRMED_EVENT_UPDATE';
  trigger_label: string; // Human-readable label

  // Approval lifecycle
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'disabled';
  status_label: string;

  meta_template_id?: string | null;
  rejection_reason?: string | null;

  // Metrics
  last_sent_at?: string | null;
  total_sent: number;

  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface FacebookConversation {
  id: string; // UUID in Django → string in TS
  organization_id: string; // FK to organization
  facebook_page_id: string;
  facebook_page_meta_id: string;
  facebook_page_name: string;
  organization: Organization; // FK to organization
  page_id: string; // FK to FacebookPage
  last_message: FacebookLastMessage | null;
  is_active: boolean;
  can_send_free_text: boolean;
  messaging_window_expires_at: string | null;
  requires_utility_message: boolean;
  lead: Lead;
  sender_id?: string | null;
  sender_name?: string | null;
  sender_profile_picture?: string | null;
  created_at: string; // ISO string
  last_activity: string; // ISO string
  ai_chat_responses: boolean;
  metadata?: Record<string, any>; // JSONField
}

// messages
export interface WhatsappMessage {
  id: string;
  conversation: string; // numeric FK from Django
  direction: 'IN' | 'OUT';
  sender: string; // e.g. "447123456787"
  body: string;
  whatsapp_message_id: string | null;
  status: 'queued' | 'sent' | 'delivered' | 'read' | 'failed';
  error_code: number | null;
  raw_payload: {
    from?: string;
    id?: string;
    timestamp?: string;
    text?: {
      body?: string;
    };
    type?: string;
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
  is_deleted?: boolean;
}

export interface FacebookMessage {
  id: string;
  sender?: 'user' | 'bot' | 'agent';
  sender_id?: string | null;
  direction: 'incoming' | 'outgoing' | 'system';
  text: string | null;
  status?: 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'bounced' | 'rejected';
  delivery_status?: 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'bounced' | 'rejected';
  external_message_id?: string | null;
  error_code?: number | null;
  last_error?: string | null;
  raw_payload?: Record<string, any>;
  created_at: string;
  updated_at?: string;
  timestamp?: string | null;
  is_deleted?: boolean;
  metadata?: Record<string, any>;
  preview_text?: string | null;
}

export interface InstagramMessage {
  id: string;
  // conversation: number;
  sender: 'user' | 'bot' | 'agent';
  direction: 'inbound' | 'outbound';
  text: string | null;
  attachments?: Array<Record<string, any>> | null;
  whatsapp_message_id: string | null;
  status: 'pending' | 'queued' | 'sent' | 'delivered' | 'read' | 'failed';
  error_code: number | null;
  raw_payload: Record<string, any>;
  timestamp: string;
  updated_at: string;
  is_deleted: boolean;
}

export interface AnalyticsOverview {
  total_leads: number;
  conversion_rate_percent: number;
  new_leads_this_week: number;
  new_leads_last_week: number;
  follow_ups_scheduled: number;
  highly_interested_leads: number;
  top_sales_rep: {
    assigned_to__user__username: string;
    lead_count: number;
  } | null; // in case no top rep exists
  stage_distribution: {
    stage: string;
    count: number;
  }[];
  interest_distribution: {
    level_of_interest: string;
    count: number;
  }[];
}

export interface Employee {
  id: string;
  role: string;
  date_joined: string;
  user: User;
  organization: Organization;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  start_date: string;
  due_date: string;
  completed_at: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  remarks: string;
  created_by: number;
  created_by_name: string;
  assigned_to: string | null;
  assigned_to_name?: string;
  organization: string;
  created_at: string;
  updated_at: string;
}

export interface Order {
  id: string;
  reference?: string;
  organization?: Organization | string;
  customer?: Customer | null;
  lead?: Lead | null;
  created_by?: string | null;
  assigned_to?: string | null;
  source?: 'wa' | 'fb' | 'insta' | 'web' | 'phone' | 'other' | string;
  status?:
    | 'draft'
    | 'pending'
    | 'confirmed'
    | 'processing'
    | 'shipped'
    | 'completed'
    | 'cancelled'
    | 'returned'
    | string;
  payment_status?:
    | 'unpaid'
    | 'partially_paid'
    | 'paid'
    | 'refunded'
    | string;
  fulfillment_status?:
    | 'unfulfilled'
    | 'packed'
    | 'out_for_delivery'
    | 'delivered'
    | 'returned'
    | string;
  priority?: 'low' | 'normal' | 'high' | 'urgent' | string;
  notes?: string | null;
  internal_notes?: string | null;
  delivery_method?: 'pickup' | 'courier' | 'in_house' | 'other' | string;
  delivery_address?: string | null;
  delivery_notes?: string | null;
  requested_delivery_date?: string | null;
  confirmed_at?: string | null;
  shipped_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  currency?: string;
  subtotal_amount?: number | string;
  discount_amount?: number | string;
  tax_amount?: number | string;
  total_amount?: number | string;
  items?: OrderItem[];
  created_at?: string;
  updated_at?: string;
}

export interface OrderItem {
  id?: string | number;
  order?: string;
  product_name: string;
  description?: string | null;
  sku?: string | null;
  quantity: number;
  unit_price: number;
  total_price?: number | string;
  discount_amount?: number | string;
  tax_amount?: number | string;
  position?: number;
  fulfilled_quantity?: number;
  backordered_quantity?: number;
  created_at?: string;
  price?: number;
}

// team chat
export interface TeamChatAttachment {
  id: string;
  filename?: string;
  file: string; // URL to the uploaded file
  content_type?: string;
  size?: number;
}

export interface TeamChatMessage {
  id: string;
  conversation: string;
  sender: string | null; // employee ID
  sender_user?: User | null;
  content?: string;
  message_type: 'text' | 'image' | 'file' | 'system';
  metadata?: Record<string, any> | null;
  attachments?: TeamChatAttachment[];
  created_at: string; // ISO datetime
}

export interface TeamChatMember {
  id: string;
  conversation: string;
  employee: string;
  is_admin: boolean;
  joined_at: string;
}

export interface TeamChatConversation {
  id: string;
  organization: string;
  recipient_name: string;
  name?: string | null;
  is_group: boolean;
  is_general: boolean;
  created_by: string;
  last_message?: TeamChatMessage | null;
  created_at: string;
  updated_at: string;
}

export interface CatalogueImage {
  id: string;
  image: string;
  created_at?: string;
  uploaded_at?: string;
}

export interface InstagramComment {
  id: string;
  comment_id: string;
  post_id: string;
  text: string;
  sender_username: string;
  timestamp: string;
  parent_comment: any;
  replies: any[];
}

export interface FacebookComment {
  id: string; // unique UUID for the comment record
  post_id: string; // ID of the post
  comment_id: string; // original Facebook comment ID
  parent_comment_id?: string | null; // parent comment ID if this is a reply
  sender_id: string; // Facebook user or page ID of sender
  sender_name: string; // name of the sender
  sender_profile_picture?: string | null; // URL of sender's profile picture
  text: string; // comment text
  created_time: string; // ISO timestamp of comment creation
  received_at?: string; // ISO timestamp when comment was received
  metadata?: Record<string, any>; // any extra metadata
  organization: string; // organization ID
  page: string; // Facebook page ID
  parent_comment?: FacebookComment; // nested replies
  replies?: FacebookComment[]; // nested replies
}
